import { useMutation, useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { trpc } from "@/utils/trpc";
import { DateFilterField } from "./date-filter-field";

type ExportSignInsFormProps = {
  allowSiteSelection?: boolean;
  siteId?: string;
  onExportComplete?: () => void;
  className?: string;
  defaultStartDate?: Date;
  defaultEndDate?: Date;
  workerId?: string;
};

export function ExportSignInsForm({
  allowSiteSelection = true,
  siteId,
  onExportComplete,
  className,
  defaultStartDate,
  defaultEndDate,
  workerId,
}: ExportSignInsFormProps) {
  const managedSites = useQuery({
    ...trpc.getManagedSites.queryOptions(),
    enabled: allowSiteSelection,
  });

  const [selectedSiteId, setSelectedSiteId] = useState<string>(siteId ?? "all");
  const [startDate, setStartDate] = useState<Date | undefined>(
    defaultStartDate
  );
  const [endDate, setEndDate] = useState<Date | undefined>(defaultEndDate);

  useEffect(() => {
    if (!allowSiteSelection && siteId) {
      setSelectedSiteId(siteId);
    }
  }, [allowSiteSelection, siteId]);

  useEffect(() => {
    setStartDate(defaultStartDate);
  }, [defaultStartDate]);

  useEffect(() => {
    setEndDate(defaultEndDate);
  }, [defaultEndDate]);

  const exportMutation = useMutation({
    ...trpc.exportWorkerSignIns.mutationOptions(),
    onSuccess: (data) => {
      if (!data.csv) {
        toast.info("No sign-ins found for the selected filters");
        return;
      }

      const blob = new Blob([data.csv], {
        type: "text/csv;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = data.fileName ?? "worker-signins.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("Export ready");
      onExportComplete?.();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const normalizedStartDate = startDate
    ? format(startDate, "yyyy-MM-dd")
    : undefined;
  const normalizedEndDate = endDate ? format(endDate, "yyyy-MM-dd") : undefined;

  const handleExport = () => {
    const resolvedSiteId =
      allowSiteSelection && selectedSiteId === "all"
        ? undefined
        : selectedSiteId || siteId || undefined;

    exportMutation.mutate({
      siteId: resolvedSiteId,
      startDate: normalizedStartDate,
      endDate: normalizedEndDate,
      workerId: workerId || undefined,
    });
  };

  return (
    <div className={cn("grid gap-3", className)}>
      {allowSiteSelection ? (
        <div className="flex flex-col gap-1 min-w-[160px]">
          <label
            htmlFor="export-site-filter"
            className="text-xs font-medium text-muted-foreground"
          >
            Site
          </label>
          <Select
            value={selectedSiteId}
            onValueChange={setSelectedSiteId}
            disabled={managedSites.isLoading}
          >
            <SelectTrigger
              id="export-site-filter"
              className="h-10 w-full"
              disabled={managedSites.isLoading}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sites</SelectItem>
              {(managedSites.data ?? []).map((site) => (
                <SelectItem key={site.id} value={site.id}>
                  {site.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      <DateFilterField
        id="export-start-date"
        label="Start date"
        placeholder="Select date"
        date={startDate}
        onChange={setStartDate}
      />
      <DateFilterField
        id="export-end-date"
        label="End date"
        placeholder="Select date"
        date={endDate}
        onChange={setEndDate}
      />

      <div className="flex flex-col gap-1">
        <span className="sr-only">Export sign-ins</span>
        <Button onClick={handleExport} disabled={exportMutation.isPending}>
          {exportMutation.isPending ? "Exporting…" : "Export CSV"}
        </Button>
      </div>
    </div>
  );
}
